import { useCallback, useEffect, useState } from 'react';

/** Turns a photo key into a full-size URL (already known, or fetched). */
export type PhotoUrlResolver = (key: string) => string | Promise<string>;

interface ViewerState {
    keys: string[];
    index: number;
    resolve: PhotoUrlResolver;
}

/**
 * State for `PhotoViewerModal` on a work order: opens a photo within its
 * group (the keys in display order), steps through the group and resolves
 * each photo's URL. The same on the admin modal and the guest page.
 */
export function usePhotoViewer() {
    const [state, setState] = useState<ViewerState | null>(null);
    const [url, setUrl] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!state) {
            setUrl(null);
            setLoading(false);
            return;
        }
        let cancelled = false;
        setLoading(true);
        Promise.resolve(state.resolve(state.keys[state.index]))
            .then((resolved) => { if (!cancelled) setUrl(resolved); })
            .catch(() => { if (!cancelled) setUrl(null); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [state]);

    /** Opens `key`; `keys` is its group in display order, for previous/next. */
    const open = useCallback((keys: string[], key: string, resolve: PhotoUrlResolver) => {
        const index = keys.indexOf(key);
        setState({ keys: index < 0 ? [key] : keys, index: Math.max(index, 0), resolve });
    }, []);

    const close = useCallback(() => setState(null), []);

    const step = useCallback((delta: number) => {
        setState((prev) => prev && {
            ...prev,
            index: (prev.index + delta + prev.keys.length) % prev.keys.length
        });
    }, []);
    const prev = useCallback(() => step(-1), [step]);
    const next = useCallback(() => step(1), [step]);

    const several = !!state && state.keys.length > 1;

    return {
        open,
        isOpen: state !== null,
        viewerProps: {
            show: state !== null,
            imageUrl: url ?? '',
            loading,
            onClose: close,
            onPrev: several ? prev : undefined,
            onNext: several ? next : undefined,
            counter: several && state ? `${state.index + 1} / ${state.keys.length}` : undefined
        }
    };
}
